<script setup>
// Fase 61 — G1: a tela das atividades/tarefas do CRM dentro do card.
//
// O backend expõe 12 ações de atividades desde sempre e NADA no dashboard as
// consumia: ligação, reunião e tarefa só nasciam de automação ou de sequência.
// Esta aba fecha a junta — lista, cria e transiciona pelo store
// `pipelineActivities`, que já carrega os guards multi-tenant.
//
// ⚠️ NÃO confundir com `ActivityTab.vue`: aquele mostra o HISTÓRICO do card (o
// JSONB `item_details.activities`, escrito pelo próprio front). Aqui são linhas
// de `pipeline_activities`, com estado próprio no servidor.
//
// ⚠️ Lembretes (`PipelineActivityReminder`, itens D6b/c/d) ficam de fora de
// propósito: o canal in-app ainda não produz e o e-mail espera o outbox. A tela
// não pode prometer um envio que não sai.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import { usePipelinePermissions } from '../../../composables/usePipelinePermissions';
import { findPipelineForCard } from '../../../helpers/pipelineStageResolver';
import {
  resolveReportingTimezone,
  zonedDateTimeToUtc,
} from '../../../utils/localeFormatting';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['itemUpdated']);

const { t, locale } = useI18n();
const store = useStore();

const selectedPipeline = computed(
  () => store.getters['pipelines/getSelectedPipeline']
);
const pipelines = computed(() => store.getters['pipelines/getPipelines'] || []);
const itemPipeline = computed(() =>
  findPipelineForCard(
    pipelines.value,
    selectedPipeline.value,
    props.item?.pipeline_id
  )
);
const { canEditCard } = usePipelinePermissions(itemPipeline);

// O formulário oferece os três tipos que a auditoria mediu como os usados de
// verdade. A LISTA rotula os sete, senão uma atividade criada por automação
// (`email`, `follow_up`, `demo`, `note`) apareceria sem nome de tipo.
const FORM_ACTIVITY_TYPES = ['call', 'meeting', 'task'];

const OPEN_STATUSES = ['pending', 'in_progress'];

// O índice do backend é paginado (`clamped_per_page(default: 25)`) e esta tela
// lê só a primeira página. Um card com mais atividades do que isto ficaria com
// a lista cortada SEM aviso — o número existe aqui para que a tela possa dizer
// que está mostrando um recorte, não para paginar.
const SERVER_PAGE_SIZE = 25;

const newType = ref('call');
const newTitle = ref('');
const newScheduledAt = ref('');

// Sinalizadores, não strings traduzidas: `t()` avaliado dentro de um `catch` e
// guardado em ref congela o idioma daquele instante — a tradução tem de
// acontecer no template, que reavalia quando o locale muda.
const loadFailed = ref(false);
const createFailed = ref(false);
const transitionFailed = ref(false);
// Guarda o par id+AÇÃO, não só o id. Cross-review Codex: com só o id, o
// spinner acendia sempre no botão Concluir — inclusive quando a operação em
// curso era o Cancelar.
const transitioning = ref(null);

const uiFlags = computed(() => store.getters['pipelineActivities/uiFlags']);

// ⚠️ Correção de review: o painel troca de card SEM desmontar esta aba, e as
// escritas do store não são "a última vence" — elas só checam a CONTA. Uma
// falha (ou um sucesso) que chega depois da troca pertence a OUTRO card:
// aplicá-la aqui é erro/estado de um contexto subindo na tela de outro. Toda
// operação carimba o card em que nasceu e só age se ele ainda estiver aberto.
// Cross-review Codex: carimbar só o card não basta — duas contas podem ter um
// card com o MESMO id. Trocando de conta, a resposta da anterior passava no
// guard e aterrissava na tela da nova. O carimbo é conta + card.
const operationTag = () =>
  `${store.getters.getCurrentAccountId ?? ''}:${props.item?.id ?? ''}`;

const belongsToOpenCard = tag => operationTag() === tag;

// O store é global: outra tela pode ter carregado a lista de OUTRO card. Só o
// que pertence a este card entra aqui. (O getter já devolve [] quando a conta
// ativa mudou — o filtro por card é a segunda peneira, não a primeira.)
//
// ⚠️ Correção de review: a ordenação repete a do servidor
// (`order(scheduled_at: :asc, created_at: :desc)`, NULLS LAST do Postgres).
// Sem ela a atividade recém-criada — que o store empilha no TOPO — trocava de
// lugar sozinha na primeira releitura. O `sort` é seguro porque roda sobre o
// array NOVO devolvido pelo `filter` — ordenar o array do getter mutaria o
// estado do Vuex fora de uma mutation.
const scheduledTime = activity => {
  const parsed = Date.parse(activity?.scheduled_at || '');
  return Number.isNaN(parsed) ? null : parsed;
};

const byServerOrder = (a, b) => {
  const aAt = scheduledTime(a);
  const bAt = scheduledTime(b);
  if (aAt !== bAt) {
    if (aAt === null) return 1;
    if (bAt === null) return -1;
    return aAt - bAt;
  }
  const byCreatedAt = String(b?.created_at || '').localeCompare(
    String(a?.created_at || '')
  );
  if (byCreatedAt !== 0) return byCreatedAt;
  return Number(b?.id || 0) - Number(a?.id || 0);
};

const activities = computed(() => {
  const cardId = props.item?.id;
  if (!cardId) return [];

  return (store.getters['pipelineActivities/allActivities'] || [])
    .filter(activity => String(activity.pipeline_card_id) === String(cardId))
    .sort(byServerOrder);
});

const isLoadingList = computed(
  () => uiFlags.value.isFetching && activities.value.length === 0
);

// A página do servidor veio cheia: há mais atividades do que a tela mostra.
const isTruncated = computed(
  () => !isLoadingList.value && activities.value.length >= SERVER_PAGE_SIZE
);

// Dicionários montados com chaves LITERAIS: `t(variável)` some da varredura de
// i18n (a chave vira "órfã" na próxima limpeza) e o lint do projeto o sinaliza.
// Reuso das strings que já existem em `SEQUENCE_DEFINITIONS` — são as mesmas.
const typeLabels = computed(() => ({
  call: t('PIPELINE.SEQUENCE_DEFINITIONS.ACTIVITY_TYPE_CALL'),
  email: t('PIPELINE.SEQUENCE_DEFINITIONS.ACTIVITY_TYPE_EMAIL'),
  meeting: t('PIPELINE.SEQUENCE_DEFINITIONS.ACTIVITY_TYPE_MEETING'),
  task: t('PIPELINE.SEQUENCE_DEFINITIONS.ACTIVITY_TYPE_TASK'),
  note: t('PIPELINE.SEQUENCE_DEFINITIONS.ACTIVITY_TYPE_NOTE'),
  demo: t('PIPELINE.SEQUENCE_DEFINITIONS.ACTIVITY_TYPE_DEMO'),
  follow_up: t('PIPELINE.SEQUENCE_DEFINITIONS.ACTIVITY_TYPE_FOLLOW_UP'),
}));

const statusLabels = computed(() => ({
  pending: t('PIPELINE.CRM_ACTIVITIES.STATUS_PENDING'),
  in_progress: t('PIPELINE.CRM_ACTIVITIES.STATUS_IN_PROGRESS'),
  completed: t('PIPELINE.CRM_ACTIVITIES.STATUS_COMPLETED'),
  cancelled: t('PIPELINE.CRM_ACTIVITIES.STATUS_CANCELLED'),
}));

const typeOptions = computed(() =>
  FORM_ACTIVITY_TYPES.map(type => ({
    value: type,
    label: typeLabels.value[type],
  }))
);

const typeLabel = activity =>
  typeLabels.value[activity?.activity_type] || activity?.activity_type || '';

const statusLabel = activity =>
  statusLabels.value[activity?.status] || activity?.status || '';

const statusClass = activity => {
  switch (activity?.status) {
    case 'completed':
      return 'bg-n-teal-3 text-n-teal-11';
    case 'cancelled':
      return 'bg-n-alpha-2 text-n-slate-11';
    case 'in_progress':
      return 'bg-n-brand/10 text-n-brand';
    default:
      return 'bg-n-amber-3 text-n-amber-11';
  }
};

const canTransition = activity =>
  canEditCard.value && OPEN_STATUSES.includes(activity?.status);

const reportingTimezone = computed(() => resolveReportingTimezone(store));

const userLocale = () =>
  String(locale.value || navigator.language || 'en-US').replace('_', '-');

// Data sempre no fuso da CONTA (`reporting_timezone`), nunca em UTC cru: é a
// regra do projeto e a origem de um bug real de agendamento.
const formatDate = value => {
  if (!value) return '';
  try {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return new Intl.DateTimeFormat(userLocale(), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: reportingTimezone.value,
    }).format(parsed);
  } catch {
    return '';
  }
};

const canSubmit = computed(
  () =>
    canEditCard.value && !!newTitle.value.trim() && !uiFlags.value.isCreating
);

const load = async () => {
  const accountId = store.getters.getCurrentAccountId;
  const tag = operationTag();
  const cardId = props.item?.id;
  if (!accountId || !cardId) return;

  loadFailed.value = false;
  try {
    await store.dispatch('pipelineActivities/get', {
      accountId,
      pipelineCardId: cardId,
    });
  } catch {
    // Mesma regra das escritas: falha de um card que já não está aberto não
    // acende o erro da tela atual.
    if (belongsToOpenCard(tag)) loadFailed.value = true;
  }
};

const isVisible = activityId =>
  activities.value.some(activity => String(activity.id) === String(activityId));

const createActivity = async () => {
  if (!canSubmit.value) return;

  const accountId = store.getters.getCurrentAccountId;
  const tag = operationTag();
  const cardId = props.item?.id;
  if (!accountId || !cardId) return;

  createFailed.value = false;
  // O controller lê `pipeline_card_id` na raiz e exige o objeto `activity`
  // (required_parameter_object!). Enviar os campos soltos devolveria 400.
  const activity = {
    activity_type: newType.value,
    title: newTitle.value.trim(),
  };
  // O input dá um horário de PAREDE ('2026-09-01T14:30'), que sozinho não
  // identifica um instante. Duas tentativas anteriores erraram por caminhos
  // opostos: `new Date(...)` resolvia no fuso do NAVEGADOR, e mandar a string
  // crua fazia o Rails resolver em UTC — `Time.use_zone(account_time_zone)`
  // existe só em dois controllers, e o de atividades não é um deles. Como a
  // lista reexibe no fuso da CONTA, o horário salvo reaparecia deslocado na
  // própria tela que acabou de gravá-lo.
  //
  // `zonedDateTimeToUtc` é o conversor que o módulo já usa (agenda, follow-ups,
  // CardInfoPanel): resolve a parede no fuso da conta e devolve o instante —
  // e devolve `null` quando aquele horário NÃO EXISTE no salto de horário de
  // verão, em vez de empurrar para outro horário sem avisar.
  if (newScheduledAt.value) {
    const [date, time] = newScheduledAt.value.split('T');
    const instant = zonedDateTimeToUtc(date, time, reportingTimezone.value);
    if (!instant) {
      createFailed.value = true;
      return;
    }
    activity.scheduled_at = instant.toISOString();
  }

  try {
    const created = await store.dispatch('pipelineActivities/create', {
      accountId,
      pipeline_card_id: cardId,
      activity,
    });
    // Resposta descartada pelos guards (troca de conta com request em voo) não
    // pode limpar o formulário nem anunciar sucesso. O mesmo vale para a troca
    // de CARD: o rascunho na tela já é de outro card.
    if (!created || !belongsToOpenCard(tag)) return;

    newTitle.value = '';
    newScheduledAt.value = '';
    // ⚠️ Correção de review: o store só insere a atividade na coleção quando a
    // lista já é comprovadamente desta conta (`loadedAccountId`, preenchido
    // apenas por uma LEITURA bem-sucedida). Se a carga inicial falhou, a
    // atividade criada não apareceria em lugar nenhum — o formulário limpava e
    // a tela seguia no erro. O store marca `isStale` justamente esperando que a
    // TELA releia; é o que fazemos quando o item novo não está visível.
    if (!isVisible(created.id)) await load();
    emit('itemUpdated');
  } catch {
    if (belongsToOpenCard(tag)) createFailed.value = true;
  }
};

const runTransition = async (activity, action) => {
  if (!canTransition(activity) || transitioning.value) return;

  const accountId = store.getters.getCurrentAccountId;
  const tag = operationTag();
  const cardId = props.item?.id;
  if (!accountId || !cardId) return;

  transitionFailed.value = false;
  transitioning.value = { id: activity.id, action, tag };
  try {
    // `pipelineCardId` viaja com a transição: o controller resolve o card por
    // ele mesmo nas rotas de membro — sem o campo a resposta é 404.
    const updated = await store.dispatch('pipelineActivities/transition', {
      accountId,
      id: activity.id,
      action,
      pipelineCardId: cardId,
    });
    if (updated && belongsToOpenCard(tag)) emit('itemUpdated');
  } catch {
    if (belongsToOpenCard(tag)) transitionFailed.value = true;
  } finally {
    // Só libera se ninguém tomou o lugar: a troca de card já zerou o indicador
    // (ver o watch), e um `finally` atrasado não pode apagar uma transição nova.
    // Cross-review Codex: soltar só pelo id ignora de QUAL contexto era o
    // pedido. Saindo e voltando ao mesmo card, um `finally` atrasado liberava a
    // transição NOVA e reabilitava os botões com ela ainda em voo.
    if (
      transitioning.value?.id === activity.id &&
      transitioning.value?.tag === tag
    ) {
      transitioning.value = null;
    }
  }
};

const completeActivity = activity => runTransition(activity, 'complete');
const cancelActivity = activity => runTransition(activity, 'cancel');

// Callback síncrono envolvendo o `load()` assíncrono: passar a função async
// direto faz o Vue encaminhar a rejeição ao error handler da aplicação antes de
// o nosso estado de erro chegar à tela.
// Observa o CARIMBO (conta + card), não só o id do card. Cross-review Codex: o
// componente é reusado entre contas, e duas contas podem ter um card com o
// mesmo id — trocando de conta, o watcher de id nunca disparava, a lista da
// conta anterior continuava na tela e o rascunho dela seria enviado para a
// conta nova. É o mesmo par conta+card que já guarda as respostas em voo; usar
// um critério aqui e outro lá é justamente como o furo apareceu.
watch(
  () => operationTag(),
  () => {
    // Erro, rascunho e indicador de transição pertencem ao card em que
    // nasceram. Trocou de card, a tela começa limpa — senão o usuário vê a
    // falha (ou o rascunho) do card anterior sobre o novo, e as ações do novo
    // nascem travadas por uma transição que já não é dele.
    loadFailed.value = false;
    createFailed.value = false;
    transitionFailed.value = false;
    transitioning.value = null;
    newTitle.value = '';
    newScheduledAt.value = '';
    load();
  },
  { immediate: true }
);
</script>

<template>
  <div class="space-y-5">
    <div>
      <h3 class="text-heading-3 text-n-slate-12">
        {{ t('PIPELINE.CRM_ACTIVITIES.TITLE') }}
      </h3>
      <p class="mt-1 text-sm text-n-slate-11">
        {{ t('PIPELINE.CRM_ACTIVITIES.SUBTITLE') }}
      </p>
    </div>

    <!-- Criação: tipo + o que fazer + quando. -->
    <form
      v-if="canEditCard"
      class="space-y-3 rounded-lg border border-n-weak bg-n-solid-3 p-4"
      @submit.prevent="createActivity"
    >
      <p class="text-sm font-medium text-n-slate-12">
        {{ t('PIPELINE.CRM_ACTIVITIES.NEW_TITLE') }}
      </p>

      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label class="flex flex-col gap-1">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('PIPELINE.CRM_ACTIVITIES.TYPE_LABEL') }}
          </span>
          <Select
            v-model="newType"
            name="activity-type"
            class="w-full [&>select]:min-h-11 [&>select]:w-full"
            :options="typeOptions"
            :aria-label="t('PIPELINE.CRM_ACTIVITIES.TYPE_LABEL')"
            :disabled="uiFlags.isCreating"
          />
        </label>

        <label class="flex flex-col gap-1">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('PIPELINE.CRM_ACTIVITIES.SCHEDULED_AT_LABEL') }}
          </span>
          <!-- `min-h-11` = 44px, o alvo de toque que o resto das abas do card
               (e o teste de acessibilidade da agenda) já exige. -->
          <Input
            v-model="newScheduledAt"
            type="datetime-local"
            name="activity-scheduled-at"
            custom-input-class="min-h-11"
            :disabled="uiFlags.isCreating"
          />
        </label>
      </div>

      <label class="flex flex-col gap-1">
        <span class="text-xs font-medium text-n-slate-11">
          {{ t('PIPELINE.CRM_ACTIVITIES.TITLE_LABEL') }}
        </span>
        <Input
          v-model="newTitle"
          type="text"
          name="activity-title"
          custom-input-class="min-h-11"
          :placeholder="t('PIPELINE.CRM_ACTIVITIES.TITLE_PLACEHOLDER')"
          :disabled="uiFlags.isCreating"
        />
      </label>

      <p v-if="createFailed" class="text-sm text-n-ruby-11" role="alert">
        {{ t('PIPELINE.CRM_ACTIVITIES.CREATE_ERROR') }}
      </p>

      <div class="flex justify-end">
        <Button
          type="submit"
          size="sm"
          class="min-h-11"
          :label="
            uiFlags.isCreating
              ? t('PIPELINE.SAVING')
              : t('PIPELINE.CRM_ACTIVITIES.SUBMIT')
          "
          :is-loading="uiFlags.isCreating"
          :disabled="!canSubmit"
        />
      </div>
    </form>
    <p v-else class="text-sm text-n-slate-11">
      {{ t('PIPELINE.CRM_ACTIVITIES.READ_ONLY') }}
    </p>

    <!-- Carga da lista: carregando / falhou / vazia / lista, quatro estados
         DISTINTOS. O erro não engole o que já está na tela: ele aparece como
         aviso acima da lista, e o estado "vazio" só fala quando a leitura deu
         certo e realmente não há nada — vazio e falha não são a mesma coisa. -->
    <p
      v-if="isLoadingList"
      class="py-6 text-center text-sm text-n-slate-11"
      role="status"
    >
      {{ t('PIPELINE.CRM_ACTIVITIES.LOADING') }}
    </p>

    <template v-else>
      <div v-if="loadFailed" class="space-y-2 text-center">
        <p class="text-sm text-n-ruby-11" role="alert">
          {{ t('PIPELINE.CRM_ACTIVITIES.LOAD_ERROR') }}
        </p>
        <Button
          variant="outline"
          color="slate"
          size="sm"
          class="min-h-11"
          :label="t('PIPELINE.CRM_ACTIVITIES.RETRY')"
          @click="load"
        />
      </div>

      <p v-if="transitionFailed" class="text-sm text-n-ruby-11" role="alert">
        {{ t('PIPELINE.CRM_ACTIVITIES.TRANSITION_ERROR') }}
      </p>

      <p
        v-if="!loadFailed && activities.length === 0"
        class="py-6 text-center text-sm text-n-slate-11"
      >
        {{ t('PIPELINE.CRM_ACTIVITIES.EMPTY') }}
      </p>

      <!-- `list-none` explícito: o reset do projeto não zera o marcador, então
           sem isto os bullets do navegador vazam à esquerda de cada atividade.
           É o mesmo cuidado que `CardAssigneePanel` já toma. Achado do teste de
           browser — não aparece em spec, só na tela. -->
      <ul v-if="activities.length" class="list-none space-y-2">
        <li
          v-for="activity in activities"
          :key="activity.id"
          class="rounded-lg border border-n-weak bg-n-solid-3 p-3"
          :class="{ 'opacity-60': activity.status === 'cancelled' }"
        >
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <p class="text-sm font-medium text-n-slate-12 break-words">
                {{ activity.title }}
              </p>
              <p class="mt-1 flex flex-wrap items-center gap-2 text-xs">
                <span class="text-n-slate-11">{{ typeLabel(activity) }}</span>
                <span
                  class="rounded px-1.5 py-0.5 font-medium"
                  :class="statusClass(activity)"
                >
                  {{ statusLabel(activity) }}
                </span>
                <span v-if="activity.scheduled_at" class="text-n-slate-11">
                  {{ formatDate(activity.scheduled_at) }}
                </span>
                <span v-else class="text-n-slate-10">
                  {{ t('PIPELINE.CRM_ACTIVITIES.NO_DATE') }}
                </span>
                <!-- `PIPELINE.OVERDUE` não existe em locale nenhum: a chave
                     traduzida é `PIPELINE.CARD.OVERDUE` (en + pt_BR). Antes
                     desta correção a tela imprimia a chave crua. -->
                <span v-if="activity.is_overdue" class="text-n-ruby-11">
                  {{ t('PIPELINE.CARD.OVERDUE') }}
                </span>
              </p>
            </div>

            <!-- O nome acessível repete o título da atividade: "Concluir"
                 sozinho, dez vezes na lista, não diz o que será concluído. -->
            <div
              v-if="canTransition(activity)"
              class="flex flex-shrink-0 items-center gap-2"
            >
              <Button
                size="sm"
                color="teal"
                class="min-h-11"
                :label="t('PIPELINE.CRM_ACTIVITIES.COMPLETE')"
                :aria-label="
                  t('PIPELINE.CRM_ACTIVITIES.COMPLETE_ARIA', {
                    title: activity.title,
                  })
                "
                :is-loading="
                  transitioning?.id === activity.id &&
                  transitioning?.action === 'complete'
                "
                :disabled="transitioning !== null"
                @click="completeActivity(activity)"
              />
              <Button
                variant="outline"
                color="slate"
                size="sm"
                class="min-h-11"
                :label="t('PIPELINE.CANCEL')"
                :aria-label="
                  t('PIPELINE.CRM_ACTIVITIES.CANCEL_ARIA', {
                    title: activity.title,
                  })
                "
                :is-loading="
                  transitioning?.id === activity.id &&
                  transitioning?.action === 'cancel'
                "
                :disabled="transitioning !== null"
                @click="cancelActivity(activity)"
              />
            </div>
          </div>
        </li>
      </ul>

      <p v-if="isTruncated" class="text-center text-xs text-n-slate-10">
        {{
          t('PIPELINE.CRM_ACTIVITIES.TRUNCATED', { count: SERVER_PAGE_SIZE })
        }}
      </p>
    </template>
  </div>
</template>
